import { useCallback } from 'react';
import { useServices } from '@/app/services';
import type { NotePatch } from '@/data/notes';
import { DELETE_GRACE_MS } from '@/data/sync';
import type { LocalNote, NoteKind } from '@/domain/note';
import { useSnackbar } from '@/ui/snackbar';

/** Note mutations that also schedule a sync and offer undo. */
export function useNoteActions() {
  const { repo, sync, sessions } = useServices();
  const snackbar = useSnackbar();

  const create = useCallback(
    async (kind: NoteKind, content?: string) => {
      const init: NotePatch = content === undefined ? {} : { content };
      const note = await repo.create(kind, sessions.get()?.user.id ?? '', init);
      sync.schedule();
      return note;
    },
    [repo, sync, sessions],
  );

  const update = useCallback(
    async (id: string, patch: NotePatch) => {
      if (await repo.update(id, patch)) sync.schedule();
    },
    [repo, sync],
  );

  const toggleTask = useCallback(
    async (id: string, line: number) => {
      if (await repo.toggleTask(id, line)) sync.schedule();
    },
    [repo, sync],
  );

  const remove = useCallback(
    async (note: LocalNote) => {
      await repo.remove(note.id);
      sync.schedule(DELETE_GRACE_MS + 500);
      const leaving = note.role !== 'owner';
      snackbar({
        text: leaving ? 'Geteilte Notiz verlassen' : 'Notiz gelöscht',
        action: {
          label: 'Rückgängig',
          onClick: () => {
            void repo.restore(note).then(() => sync.schedule());
          },
        },
        duration: DELETE_GRACE_MS - 500,
      });
    },
    [repo, sync, snackbar],
  );

  const setArchived = useCallback(
    async (note: LocalNote, archived: boolean) => {
      await update(note.id, { archived });
      snackbar({
        text: archived ? 'Notiz archiviert' : 'Notiz wiederhergestellt',
        action: { label: 'Rückgängig', onClick: () => void update(note.id, { archived: !archived }) },
      });
    },
    [update, snackbar],
  );

  return { create, update, toggleTask, remove, setArchived };
}
